// TODO ISSUE: Remove this when creating the logic for this component/view
/* istanbul ignore file */

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { IPageContentDto } from 'src/app/core/dtos/interfaces/i-page-content-dto';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { WithLanguagesFormSection } from 'src/app/shared/mixins/with-languages-form-section';
import { WithWebsitesFormSection } from 'src/app/shared/mixins/with-websites-form-section';
import { FORM_FIELDS } from './constants/form-fields';
import { FORM_PATHS } from './constants/form-paths';
import { ActivatedRoute, Router } from '@angular/router';
import { PageContentTypeService } from 'src/app/content/services/page-content-type.service';
import { WithContentBodyFormSection } from 'src/app/shared/mixins/with-content-body-form-section';
import { WithHeadlineFormSection } from 'src/app/shared/mixins/with-headline-form-section';
import { WithNextStateFormSection } from 'src/app/shared/mixins/with-next-states-form-section';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { WithDeactivateGuard } from 'src/app/shared/mixins/with-deactivate-guard';
import { WithStandfirstFormSection } from 'src/app/shared/mixins/with-standfirst-form-section';
import { IAllowableState } from 'src/app/core/dtos/interfaces/i-allowable-state';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { PageContentDto } from 'src/app/core/dtos/page-content-dto';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { IWebsiteUrl } from 'src/app/core/dtos/interfaces/i-website-url';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { IContentStateChangeResponseDto } from 'src/app/core/dtos/interfaces/i-content-state-change-response-dto';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { UrlService } from 'src/app/shared/services/url.service';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';

class PageContentFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
    public _dialogService: DialogService,
    public signalRService: SignalrService
  ) {}
}

const PageContentFormComponentMixinBase = WithProcessing(
  WithDeactivateGuard(
    WithHeadlineFormSection(
      WithStandfirstFormSection(
        WithContentBodyFormSection(
          WithNextStateFormSection(
            WithWebsitesFormSection(
              WithLanguagesFormSection(PageContentFormComponentBase)
            )
          )
        )
      )
    )
  )
);

@Component({
  selector: 'app-page-content-form',
  templateUrl: './page-content-form.component.html',
  styleUrls: ['./page-content-form.component.scss'],
})
export class PageContentFormComponent
  extends PageContentFormComponentMixinBase
  implements OnInit, OnDestroy
{
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  formReady = new BehaviorSubject<boolean>(false);

  isSaving = false;
  isProcessingStateChange = false;
  isEditMode = false;
  isNewPage = false;

  id: string;
  pageHeader: string;
  pageContent$!: Observable<IPageContentDto>;
  initialPageContent!: IPageContentDto;
  currentUserName: string = '';

  form = this.formBuilder.group({
    [this.FORM_FIELDS.HEADLINE]: ['', Validators.required],
    [this.FORM_FIELDS.STANDFIRST]: [''],
    [this.FORM_FIELDS.LANGUAGE]: [null],
    [this.FORM_FIELDS.CONTENT_BODY]: this.formBuilder.group({
      [this.FORM_FIELDS.BODY]: [],
      [this.FORM_FIELDS.IMGS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.VIDEOS]: this.formBuilder.array([]),
    }),
    [this.FORM_FIELDS.WEBSITES]: this.formBuilder.group(
      {},
      {
        validators: [this._initWebsiteValidation()],
      }
    ),
    [this.FORM_FIELDS.INTERNAL_INFORMATION]: this.formBuilder.group({
      [this.FORM_FIELDS.LIVE_URLS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.CONTENT_IDS]: [],
    }),
    [this.FORM_FIELDS.ARTICLE_VERSION_LIST]: this.formBuilder.array([]),
    [this.FORM_FIELDS.NEXT_STATES]: [],
  });

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): void {
    this.handleBeforeUnload(event);
  }

  constructor(
    public formBuilder: FormBuilder,
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public pageService: PageContentTypeService,
    public signalRService: SignalrService,
    public urlService: UrlService,
    public _dialogService: DialogService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _userSettingsService: UserSettingsService
  ) {
    super(
      languageService,
      deliveryTargetsService,
      formBuilder,
      _dialogService,
      signalRService
    );
  }

  ngOnInit(): void {
    this._routerUrlSub();

    if (this.isNewPage) {
      this.pageHeader = 'Create Page';
      this.initialPageContent = new PageContentDto({});
      this.initialPageContent.dynamicData.allowableNextStates = [];
      this.initialPageContent.dynamicData.versions = [];
      this.initialPageContent.dynamicData.liveUrls = [];
      this.initialPageContent.dynamicData.previewUrls = [];

      this._userSettingsService.getForCurrentUser$().subscribe((x) => {
        this.initialPageContent.languageCode = x.defaultLanguageCode;
        this.initialPageContent.deliveryTargetCodes =
          x.defaultDeliveryTargetCodes ?? [];
        this._initForm();
      });
    } else {
      this.pageHeader = 'Edit Page';
      this._routeParamsSub();
    }

    if (!this.isEditMode && !this.isNewPage) {
      this.form.disable();
    }

    this._userSettingsService.getCurrentUserName$().subscribe((username) => {
      this.currentUserName = username;
    });
  }

  private _routerUrlSub(): void {
    this._activatedRoute.url.subscribe((path) => {
      const segments = path.map((segment) => segment.path);
      this.isEditMode =
        segments.length > 0 &&
        (segments.includes('edit') || segments.includes('new'));

      this.isNewPage = segments.length > 0 && segments.includes('new');
    });
  }

  markAsPristine() {
    // Existing content bodies have some weird formating in them which the new text editor automatically fixes
    // This causes the form body to be dirtied with no user input
    // So we mark the form as pristine again and update the initial body value with the auto-formatted one
    this.initialPageContent.body = this.form.get(FORM_PATHS.BODY()).value;
    this.form.markAsPristine();
    this.formReady.next(true);
  }

  private _routeParamsSub(): void {
    this._activatedRoute.paramMap
      .pipe(
        filter((paramMaps) => {
          return !!paramMaps.get('id');
        }),
        switchMap((paramsMap) => {
          this.id = paramsMap.get('id');
          return this.process$(this.getPageContent$(this.id));
        })
      )
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this._dialogService.openErrorDialogAndNavigate(
              {
                title: 'Page content not found',
                message:
                  'The page content was not found. It may have been deleted.',
              },
              'dashboard'
            );
          }
          this._dialogService.openErrorDialogAndNavigate(
            {
              message:
                'An error has occurred loading the page. Please contact IT.',
            },
            'dashboard'
          );
          return of(null);
        })
      )
      .subscribe({
        next: (pageContentDto: IPageContentDto) => {
          this.initialPageContent = pageContentDto;

          this.setEditMode(pageContentDto.dynamicData.canEdit);
          this._initForm();
        },
      });
  }

  private setEditMode(newEditModeValue) {
    if (newEditModeValue !== this.isEditMode) {
      newEditModeValue
        ? this.redirectToEditContent(this.id)
        : this.redirectToReadOnlyContent(this.id);
      return;
    }
    this.isEditMode = newEditModeValue;

    this.isEditMode ? this.form.enable() : this.form.disable();
  }

  private redirectToReadOnlyContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.PAGE],
      id,
    ]);
  }

  private redirectToEditContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.PAGE],
      id,
      'edit',
    ]);
  }

  private _initForm(): void {
    this._initDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
    this._initHeadline(this.initialPageContent.headline);
    this._initStandfirst(this.initialPageContent.standfirst);
    this._initLanguages(this.getCurrentSelectedLanguage);
    this._initNextStates(
      this.initialPageContent.dynamicData.allowableNextStates
    );
    this._initContentBody(
      this.initialPageContent.body,
      this.initialPageContent.bodyImages,
      this.initialPageContent.bodyVideos
    );

    this._initFormValueChanges();
  }

  private _isVersionId(contentIdToCheck: string): boolean {
    return contentIdToCheck.includes('Page');
  }

  private _initFormValueChanges(): void {
    this._initLanguageValueChanges(this.getCurrentSelectedLanguage);
    this._initWebsitesValueChanges(this.getCurrentWebsites);
    this._initHeadlineValueChanges(this.getHeadline);
    this._initContentBodyValueChanges(
      this.getBody,
      this.getBodyImgs,
      this.getBodyVideos
    );
    this._initStandfirstValueChanges(this.getStandfirst);
    this._initNextStatesValueChanges(this.getNextStates);
  }

  private _updateForms(): void {
    this._updateDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
  }

  getCurrentSelectedLanguage = () => {
    return this.initialPageContent.languageCode;
  };

  getCurrentWebsites = (): string[] | null => {
    return this.initialPageContent.deliveryTargetCodes;
  };

  getCurrentUrls = (): IWebsiteUrl[] | null => {
    if (this.initialPageContent.status != ContentStatus.PUBLISHED) {
      return this.initialPageContent.deliveryTargetCodes.map((targetCode) => {
        return {
          deliveryTargetCode: targetCode,
          url: this.urlService.getPreviewUrl(
            this.initialPageContent.id,
            targetCode
          ),
        };
      });
    }
    return this.initialPageContent.dynamicData.liveUrls;
  };

  getHeadline = (): string | null => {
    return this.initialPageContent.headline;
  };

  getBody = (): string | null => {
    return this.initialPageContent.body;
  };

  getBodyImgs = (): IImgMediaDto[] | null => {
    return this.initialPageContent.bodyImages;
  };

  getBodyVideos = (): IVideoMediaDto[] | null => {
    return this.initialPageContent.bodyVideos;
  };

  getStandfirst = (): string | null => {
    return this.initialPageContent.standfirst;
  };

  getNextStates = (): IAllowableState[] | null => {
    return this.initialPageContent.dynamicData.allowableNextStates;
  };

  createNewVersion(): void {
    this.isProcessingStateChange = true;
    this.pageService
      .createVersion$(
        this.initialPageContent.contentId,
        this.initialPageContent.id
      )
      .pipe(
        catchError(() => {
          this._dialogService.openErrorDialog({
            message:
              'An error has occurred whilst creating a new version of the page. Please contact IT.',
          });
          this.isProcessingStateChange = false;
          return of(null);
        })
      )
      .subscribe(() => {
        this._snackBar.open(`New page version created successfully`, 'Save', {
          duration: 5 * 1000,
        });
        this.isProcessingStateChange = false;
        this.redirectToEditContent(this.initialPageContent.contentId);
      });
  }

  onUpdateState(stateAction: ContentStatus): void {
    const obs$ =
      stateAction === ContentStatus.PUBLISHED
        ? this._dialogService
            .openConfirmationDialog({
              title: 'Publish page?',
              message:
                'This will make it live on the websites you have selected.',
              confirmLabel: 'Yes',
              cancelLabel: 'No',
            })
            .afterClosed()
            .pipe(
              filter((dialogResponse) => !!dialogResponse),
              switchMap((_) => {
                return this.performStateChange(stateAction);
              })
            )
        : this.performStateChange(stateAction);

    obs$.subscribe();
  }

  private performStateChange(
    stateAction: ContentStatus
  ): Observable<IPageContentDto> {
    let action = stateAction.toString();
    this.isProcessingStateChange = true;
    return this.process$(
      this.pageService.updateState$(this.initialPageContent.id, action)
    ).pipe(
      catchError(() => {
        this._dialogService.openErrorDialog({
          message:
            'An error has occurred whilst saving the page. Please contact IT.',
        });
        this.isProcessingStateChange = false;
        return of(null);
      }),
      switchMap((contentStateChange) => {
        this.isProcessingStateChange = false;
        this._snackBar.open(`Page moved to ${stateAction} successfully`, null, {
          duration: 5 * 1000,
        });

        if (contentStateChange.currentStateEditable !== this.isEditMode) {
          this.setEditMode(contentStateChange.currentStateEditable);
          return of(null);
        } else {
          return this.getPageContent$(this.initialPageContent.id);
        }
      }),
      tap((refreshedDto: IPageContentDto) => {
        this.initialPageContent = refreshedDto;
        this.markAsPristine();
      })
    );
  }

  onSave(): void {
    if (this.isNewPage) {
      this.isSaving = true;
      this.createNewPage();
    } else {
      let missingWebsites = this.getCurrentWebsites().filter(
        (website) => !this._buildDeliveryTargetCodes().includes(website)
      );
      const obs$ =
        missingWebsites.length > 0
          ? this.deliveryTargets$.pipe(
              map((dt) =>
                dt
                  .filter((item) => missingWebsites.includes(item.id))
                  .map((item) => item.label)
              ),
              switchMap((displayList) => {
                return this._dialogService
                  .openConfirmationDialog({
                    title: 'Save page?',
                    message:
                      `This change will remove content from the following websites <br><br>` +
                      `${displayList.join(', ')}<br><br>` +
                      `Are you sure you want to continue to save?<br>` +
                      `Note: The links in emails will break for removed sites`,
                    confirmLabel: 'Yes',
                    cancelLabel: 'No',
                  })
                  .afterClosed()
                  .pipe(filter((dialogResponse) => !!dialogResponse));
              }),
              switchMap((_) => {
                return this.performPageSave$();
              })
            )
          : this.performPageSave$();

      obs$.subscribe();
    }
  }

  private performPageSave$(): Observable<IPageContentDto> {
    let newPageContent = this.buildPage();
    this.isSaving = true;
    return this.pageService
      .update$(this.initialPageContent.contentId, newPageContent)
      .pipe(
        catchError(() => {
          this._dialogService.openErrorDialog({
            message:
              'An error has occurred whilst saving the page. Please contact IT.',
          });
          this.isSaving = false;
          return of(null);
        }),
        switchMap(() => {
          return this.getPageContent$(this.initialPageContent.contentId);
        }),
        tap((refreshedDto) => {
          this._snackBar.open('Page saved successfully', null, {
            duration: 5 * 1000,
          });
          this.initialPageContent = refreshedDto;
          this.form.markAsPristine();
          this.isSaving = false;
          this._updateForms();
          this.signalRService.userSavedPage(
            this.currentUserName,
            refreshedDto.contentId
          );
        })
      );
  }

  createNewPage() {
    let newPageContent = this.buildPage();

    if (newPageContent.deliveryTargetCodes.length === 0) {
      this._dialogService.openErrorDialog({
        message: 'Website is required in order to save',
      });
    } else {
      this.isSaving = true;
      this.pageService
        .create$(newPageContent)
        .pipe(
          catchError(() => {
            this._dialogService.openErrorDialog({
              message:
                'An error has occurred whilst saving the page. Please contact IT.',
            });
            this.isSaving = false;
            return of(null);
          })
        )
        .subscribe((x: ISaveContentResponseDto | null) => {
          if (!x) {
            return;
          }
          this.form.markAsPristine();
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.PAGE],
            x.contentId,
            'edit',
          ]);
        });
    }
  }

  deletePage() {
    this._dialogService
      .openConfirmationDialog({
        title: 'Delete page?',
        message:
          'This will delete the current page and all its versions. Are you sure you want to do this?',
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return this.pageService
            .delete$(this.initialPageContent.contentId)
            .pipe(
              catchError(() => {
                this._dialogService.openErrorDialog({
                  message:
                    'An error has occurred whilst deleting the page. Please contact IT.',
                });
                return of(null);
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  deleteUnpublishedDraft() {
    this._dialogService
      .openConfirmationDialog({
        title: 'Delete unpublished draft version?',
        message:
          'This will delete the current unpublished draft version. Are you sure you want to do this?',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap((_) => {
          return this.pageService
            .deleteVersion$(
              this.initialPageContent.contentId,
              this.initialPageContent.id
            )
            .pipe(
              catchError(() => {
                this._dialogService.openErrorDialog({
                  message:
                    'An error has occurred whilst deleting the page. Please contact IT.',
                });
                return EMPTY;
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  private getPageContent$(id: string) {
    if (this._isVersionId(id)) {
      this.pageContent$ = this.process$(this.pageService.getByVersionId$(id));
    } else {
      this.pageContent$ = this.process$(this.pageService.getById$(id));
    }

    return this.pageContent$;
  }

  private buildPage(): PageContentDto {
    const newPageContent = new PageContentDto({});
    Object.assign(newPageContent, this.initialPageContent);
    newPageContent.headline = this._buildHeadline();
    newPageContent.body = this._buildBody();
    newPageContent.bodyImages = this._buildBodyImages();
    newPageContent.bodyVideos = this._buildBodyVideos();
    newPageContent.deliveryTargetCodes = this._buildDeliveryTargetCodes();
    newPageContent.languageCode = this._buildLanguage();
    newPageContent.standfirst = this._buildStandfirst();

    return newPageContent;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
